import type { stats } from "@app/domain";
import type { PlayerCard as Card, Member } from "@app/protocol";
import type { Ref } from "react";
import { openExternal } from "../discord/handshake";
import { useT } from "../i18n/context";
import { intlLocale } from "../i18n/index";
import { useStore } from "../store";
import { Avatar } from "./Avatar";
import { discordProfileUrl } from "./discordProfile";
import { Icon } from "./icons";
import { usePlayerCard } from "./usePlayerCard";

/** An action under the card: the owner's "Remove from table" in the lobby (decision 99). */
export interface CardAction {
  label: string;
  onClick: () => void;
}

/** "Open Discord profile" on someone else's card (decision 151); Discord asks before it opens. */
function DiscordProfile({ userId }: { userId: string }) {
  const { t } = useT();
  const sdk = useStore((s) => s.sdk);
  const me = useStore((s) => s.member?.userId ?? null);
  const url = discordProfileUrl(userId);
  if (url === null || userId === me) return null;
  return (
    <button
      type="button"
      className="btn-glass h-11 rounded-pill font-bold"
      onClick={() => void openExternal(sdk, url)}
    >
      {t("playerCard.discord")}
    </button>
  );
}

function Numbers({ card }: { card: Card }) {
  const { t, locale } = useT();
  if (card.matches === 0)
    return <p className="m-0 text-[13px] text-ink/80">{t("playerCard.none")}</p>;
  const percent = (rate: number) =>
    new Intl.NumberFormat(intlLocale(locale), { style: "percent" }).format(rate / 100);
  const line = [
    t("profile.record", { matches: card.matches, wins: card.wins }),
    ...(card.rate === null ? [] : [t("playerCard.rate", { rate: percent(card.rate) })]),
  ].join(" · ");
  // Results carry no id (decision 97); a result is its place in the list.
  const results = card.recent.map((match, place) => ({
    ...match,
    place,
    name: t("playerCard.result", {
      game: t(`variant.${match.variant}`),
      score: match.score,
      result: t(match.won ? "playerCard.won" : "playerCard.lost"),
    }),
  }));
  return (
    <div className="flex flex-col gap-1.5">
      <p className="m-0 text-[14px] font-bold">{line}</p>
      {card.favourite === null ? null : (
        <p className="m-0 text-[13px] text-ink/80">
          {t("playerCard.favourite", { game: t(`variant.${card.favourite}`) })}
        </p>
      )}
      <span className="text-[11px] font-bold uppercase tracking-[0.08em] text-ink/60">
        {t("playerCard.recent")}
      </span>
      <ol
        aria-label={t("playerCard.recentName", { count: results.length })}
        className="m-0 flex list-none flex-wrap gap-1 p-0"
      >
        {results.map((match) => (
          <li
            key={match.place}
            className={`rounded-[8px] px-1.5 py-0.5 text-[11px] font-extrabold tabular-nums ${match.won ? "bg-success/25 text-success" : "bg-danger-soft/20 text-danger-soft"}`}
          >
            <span aria-hidden="true">
              {t(`variant.${match.variant}`).slice(0, 1)} {match.score}
            </span>
            <span className="sr-only">{match.name}</span>
          </li>
        ))}
      </ol>
      <span className="text-[11px] text-ink/55">{t("playerCard.allMatches")}</span>
    </div>
  );
}

/**
 * What a player card shows (review 0017): the picture and frame and the name from where it opened
 * (U17), then the numbers once they load (S7), and an action for the owner. The frame around it
 * decides modal or not.
 */
export function PlayerCardBody({
  member,
  rank,
  labelId,
  headingRef,
  action,
  onClose,
}: {
  member: Pick<Member, "userId" | "name" | "avatar">;
  rank: stats.RankFrame | null;
  labelId: string;
  headingRef?: Ref<HTMLHeadingElement>;
  action?: CardAction | undefined;
  onClose: () => void;
}) {
  const { t } = useT();
  const { state, retry } = usePlayerCard(member.userId);
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-3">
        <Avatar member={member} size={rank === null ? 44 : 56} rank={rank} />
        <h2
          id={labelId}
          ref={headingRef}
          tabIndex={-1}
          className="m-0 line-clamp-2 min-w-0 flex-1 break-words text-[16px] font-extrabold leading-tight outline-none"
        >
          <bdi>{member.name}</bdi>
        </h2>
        <button
          type="button"
          aria-label={t("playerCard.close")}
          className="flex h-11 w-11 flex-none items-center justify-center rounded-full text-ink/80 hover:bg-ink/10"
          onClick={onClose}
        >
          <Icon name="close" size={18} strokeWidth={2.2} />
        </button>
      </div>
      {state.status === "ready" ? <Numbers card={state.card} /> : null}
      {state.status === "loading" ? (
        <p role="status" className="m-0 text-[13px] text-ink/70">
          {t("playerCard.loading")}
        </p>
      ) : null}
      {state.status === "failed" ? (
        <div className="flex items-center justify-between gap-2">
          <p className="m-0 text-[13px] text-ink/80">{t("playerCard.failed")}</p>
          <button
            type="button"
            className="btn-glass h-11 rounded-pill px-4 font-bold"
            onClick={retry}
          >
            {t("playerCard.retry")}
          </button>
        </div>
      ) : null}
      <DiscordProfile userId={member.userId} />
      {action ? (
        <button
          type="button"
          className="h-11 rounded-pill border-[1.5px] border-danger-soft/70 font-bold text-danger-soft hover:bg-danger/20"
          onClick={action.onClick}
        >
          {action.label}
        </button>
      ) : null}
    </div>
  );
}
